import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { TERMS_PDF, PRIVACY_PDF } from '../utils/consent';

export default function AuthPage() {
  const { login } = useAuth();
  const [mode, setMode] = useState('login');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', confirmPassword: '', name: '', phone: '' });
  const [inviteCode, setInviteCode] = useState('');
  const [registered, setRegistered] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  // Session AG: forgotten-password request. `resetSent` is set on ANY 200 —
  // the backend deliberately returns the same response whether or not the
  // address belongs to an account, so the UI must not imply it knows either.
  const [resetSent, setResetSent] = useState(false);

  // Session V (A2): the legacy ?agency= query param is gone. It fed straight
  // into the register call, so anyone could pick their own agency by editing
  // the URL and land inside another agency's staff list. An invite code —
  // validated server-side — is now the only way to join anything but agency 1.
  useEffect(() => {
    // Check for invite code in path: /join/CODE
    const pathParts = window.location.pathname.split('/');
    const joinIndex = pathParts.indexOf('join');
    if (joinIndex !== -1 && pathParts[joinIndex + 1]) {
      setInviteCode(pathParts[joinIndex + 1]);
      setMode('register');
    }
  }, []);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleLogin = async e => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await login(form.email, form.password);
      if (window.location.pathname.startsWith('/join/')) {
        window.history.replaceState(null, '', '/');
      }
    }
    catch (err) {
      // Session AJ: this used to be a bare `catch` that showed
      // "Invalid email or password." for EVERY failure — including a 429 from
      // loginEmailLimiter (10 attempts per email per 15 min) and a 500. A
      // locked-out staff member was told their password was wrong and kept
      // retrying, which is exactly the wrong advice. 401 keeps the vague
      // wording on purpose (the backend returns a generic 'Invalid
      // credentials' so the screen cannot reveal whether the address exists);
      // every other status shows what the server actually said.
      const status = err.response?.status;
      setError(
        status === 401
          ? 'Invalid email or password.'
          : err.response?.data?.error || 'Could not sign in. Please try again.'
      );
    }
    finally { setLoading(false); }
  };

  const handleForgot = async e => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await axios.post('/api/auth/forgot-password', { email: form.email });
      setResetSent(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not send the reset email. Please try again.');
    } finally { setLoading(false); }
  };

  const handleRegister = async e => {
    e.preventDefault();
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match. Please try again.');
      return;
    }
    if (!agreedToTerms) {
      setError('Please accept the Terms & Conditions and Privacy Policy to continue.');
      return;
    }
    setError(''); setLoading(true);
    try {
      await axios.post('/api/auth/register', {
        email: form.email,
        password: form.password,
        name: form.name,
        phone: form.phone,
        invite_code: inviteCode || undefined,
      });
      // Redirect to root URL so PWA install prompt is on the correct domain
      window.history.replaceState(null, '', '/');
      setRegistered(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed.');
    } finally { setLoading(false); }
  };

  if (mode === 'forgot') {
    return (
      <div className="auth-screen">
        <div className="auth-box">
          <div className="auth-logo">
            <h1>
              <span style={{color:'var(--text-primary)',fontWeight:800}}>{process.env.REACT_APP_BRAND_PREFIX||'SHIFT'}</span>
              <span style={{color:'var(--accent)',fontWeight:800}}>{process.env.REACT_APP_BRAND_SUFFIX||'on'}</span>
            </h1>
          </div>

          {resetSent ? (
            <div style={{textAlign:'center',padding:'8px 0 24px'}}>
              <div style={{fontSize:40,marginBottom:16}}>📧</div>
              <div style={{fontWeight:700,fontSize:18,marginBottom:10,color:'var(--text-primary)'}}>Check your email</div>
              {/* Deliberately worded as "if there's an account". The backend
                  answers identically for a real and an unknown address, and
                  this screen must not give away which it was. */}
              <div style={{fontSize:13,color:'var(--text-secondary)',lineHeight:1.6,marginBottom:24}}>
                If there's an account for that email address, we've sent a link to reset your password.
                It expires in 60 minutes. Remember to check your spam folder.
              </div>
              <button className="btn btn-primary" onClick={() => { setMode('login'); setResetSent(false); setError(''); }}>
                Back to Sign In
              </button>
            </div>
          ) : (
            <form onSubmit={handleForgot}>
              <div style={{textAlign:'center',marginBottom:20}}>
                <div style={{fontWeight:700,fontSize:17,marginBottom:8,color:'var(--text-primary)'}}>Forgot your password?</div>
                <div style={{fontSize:13,color:'var(--text-secondary)',lineHeight:1.6}}>
                  Enter your email address and we'll send you a link to choose a new one.
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Email Address</label>
                <input className="form-input" type="email" placeholder="johndoe@example.com"
                  value={form.email} onChange={set('email')} required />
              </div>
              {error && <p style={{color:'var(--danger)',fontSize:13,marginBottom:12}}>{error}</p>}
              <button className="btn btn-primary" type="submit" disabled={loading}>
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
              <div className="auth-switch">
                Remembered it? <button type="button" onClick={() => { setMode('login'); setError(''); }}>Sign In</button>
              </div>
            </form>
          )}
        </div>
      </div>
    );
  }

  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isAndroid = /android/i.test(navigator.userAgent);
  const isMobile = isIOS || isAndroid;

  if (registered) {
    return (
      <div className="auth-screen">
        <div className="auth-box">
          <div className="auth-logo">
            <h1>
              <span style={{color:'var(--text-primary)',fontWeight:800}}>{process.env.REACT_APP_BRAND_PREFIX||'SHIFT'}</span>
              <span style={{color:'var(--accent)',fontWeight:800}}>{process.env.REACT_APP_BRAND_SUFFIX||'on'}</span>
            </h1>
          </div>
          <div style={{textAlign:'center',padding:'8px 0 24px'}}>
            <div style={{fontSize:40,marginBottom:16}}>✓</div>
            <div style={{fontWeight:700,fontSize:18,marginBottom:10,color:'var(--text-primary)'}}>You're registered!</div>
            <div style={{fontSize:13,color:'var(--text-secondary)',lineHeight:1.6,marginBottom:24}}>
              Your account is pending approval from the coordinator. Once approved you'll be able to view and apply for shifts.
            </div>

            {isMobile && (
              <div style={{background:'var(--bg-surface)',border:'1px solid var(--border)',borderRadius:10,padding:'14px 16px',marginBottom:20,textAlign:'left'}}>
                <div style={{fontWeight:700,fontSize:13,color:'var(--text-primary)',marginBottom:6}}>📲 Install the {`${process.env.REACT_APP_BRAND_PREFIX||'SHIFT'}${process.env.REACT_APP_BRAND_SUFFIX||'on'}`} app</div>
                {isIOS ? (
                  <div style={{fontSize:12,color:'var(--text-secondary)',lineHeight:1.7}}>
                    Tap the <strong>Share button</strong> (box with arrow) at the bottom of your browser, then tap <strong>"Add to Home Screen"</strong>.
                  </div>
                ) : (
                  <div style={{fontSize:12,color:'var(--text-secondary)',lineHeight:1.7}}>
                    Tap the <strong>three dots menu</strong> in your browser, then tap <strong>"Add to Home screen"</strong>.
                  </div>
                )}
              </div>
            )}

            <button className="btn btn-primary" onClick={() => { setRegistered(false); setMode('login'); }}>
              Go to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-screen">
      <div className="auth-box">
        <div className="auth-logo">
          <h1>
              <span style={{color:'var(--text-primary)',fontWeight:800}}>{process.env.REACT_APP_BRAND_PREFIX||'SHIFT'}</span>
              <span style={{color:'var(--accent)',fontWeight:800}}>{process.env.REACT_APP_BRAND_SUFFIX||'on'}</span>
            </h1>
          <p>Event staffing platform</p>
        </div>

        {mode === 'login' ? (
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input className="form-input" type="email" placeholder="johndoe@example.com"
                value={form.email} onChange={set('email')} required />
            </div>
            <div className="form-group">
              <label className="form-label">Password</label>
              <input className="form-input" type="password" placeholder="Min. 8 characters"
                value={form.password} onChange={set('password')} required />
            </div>
            <div style={{textAlign:'right',marginTop:-8,marginBottom:12}}>
              <button type="button"
                onClick={() => { setMode('forgot'); setError(''); setResetSent(false); }}
                style={{background:'none',border:'none',padding:0,cursor:'pointer',color:'var(--accent)',fontWeight:600,fontSize:12}}>
                Forgot password?
              </button>
            </div>
            {error && <p style={{color:'var(--danger)',fontSize:13,marginBottom:12}}>{error}</p>}
            <button className="btn btn-primary" type="submit" disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
            <div className="auth-switch">
              New staff member? <button type="button" onClick={() => { setMode('register'); setError(''); }}>Create Account</button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleRegister}>
            {inviteCode && (
              <div style={{background:'var(--bg-surface)',border:'1px solid var(--border)',borderRadius:8,padding:'10px 14px',marginBottom:16,fontSize:12,color:'var(--text-secondary)'}}>
                ✓ You've been invited to join. Fill in your details below.
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Full Name</label>
              <input className="form-input" placeholder="Your full name" value={form.name} onChange={set('name')} required />
            </div>
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input className="form-input" type="email" placeholder="johndoe@example.com" value={form.email} onChange={set('email')} required />
            </div>
            <div className="form-group">
              <label className="form-label">Phone Number</label>
              <input className="form-input" placeholder="+27 82 000 0000" value={form.phone} onChange={set('phone')} required />
            </div>
            <div className="form-group">
              <label className="form-label">Password</label>
              <input className="form-input" type="password" placeholder="Min. 8 characters" value={form.password} onChange={set('password')} required minLength={8} />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm Password</label>
              <input className="form-input" type="password" placeholder="Re-enter your password" value={form.confirmPassword} onChange={set('confirmPassword')} required minLength={8} />
            </div>
            {error && <p style={{color:'var(--danger)',fontSize:13,margin:'12px 0'}}>{error}</p>}
            <div style={{display:'flex',alignItems:'flex-start',gap:10,margin:'16px 0',padding:'12px 14px',background:'var(--bg-surface)',border:'1px solid var(--border)',borderRadius:8}}>
              <input
                type="checkbox"
                id="terms-checkbox"
                checked={agreedToTerms}
                onChange={e => setAgreedToTerms(e.target.checked)}
                style={{marginTop:2,accentColor:'var(--accent)',cursor:'pointer',flexShrink:0,width:16,height:16}}
              />
                {/* Session W (P3): the filenames carry the version the consent
                    row records. The links used to point at unversioned names, so
                    replacing either PDF silently rewrote the document every past
                    consent record claimed to be for. A new version ships as a NEW
                    file. Session X: the version now lives in utils/consent.js,
                    kept in sync with server.js. */}
              <label htmlFor="terms-checkbox" style={{fontSize:12,color:'var(--text-secondary)',lineHeight:1.6,cursor:'pointer'}}>
                I have read and agree to the{' '}
                <a href={TERMS_PDF} target="_blank" rel="noopener noreferrer" style={{color:'var(--accent)',fontWeight:600,textDecoration:'underline'}}>Terms &amp; Conditions</a>
                {' '}and{' '}
                <a href={PRIVACY_PDF} target="_blank" rel="noopener noreferrer" style={{color:'var(--accent)',fontWeight:600,textDecoration:'underline'}}>Privacy Policy</a>
                {' '}of SHIFTon.
              </label>
            </div>
            <button className="btn btn-primary" type="submit" disabled={loading}>
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
            <div className="auth-switch">
              Already have an account? <button type="button" onClick={() => { setMode('login'); setError(''); }}>Sign In</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
